import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { programsData } from '../data/programsData';
import {
  FaCheckCircle,
  FaCalendarWeek,
  FaDumbbell,
  FaChartLine,
  FaWhatsapp,
  FaSearch,
  FaFire,
  FaChevronRight,
  FaSlidersH
} from 'react-icons/fa';

const ExplorePrograms = () => {
  const location = useLocation();
  const [selectedId, setSelectedId] = useState(programsData[0].id);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  const categories = ['All', 'Strength', 'Hypertrophy', 'Fat Loss', 'Beginner', 'Conditioning', 'Performance', '1-on-1 Coaching'];

  useEffect(() => {
    if (location.hash) {
      const targetId = location.hash.replace('#', '');
      const matched = programsData.find((p) => p.id === targetId);
      if (matched) {
        setSelectedId(matched.id);
        const element = document.getElementById(`card-${targetId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else if (location.state && location.state.programId) {
      const matched = programsData.find((p) => p.id === location.state.programId);
      if (matched) {
        setSelectedId(matched.id);
        const element = document.getElementById(`card-${matched.id}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location]);

  const activeProgram = programsData.find((p) => p.id === selectedId) || programsData[0];

  const filteredPrograms = programsData.filter((p) => {
    const matchesCategory = activeCategory === 'All' || p.category.toLowerCase().includes(activeCategory.toLowerCase());
    const matchesSearch =
      p.heading.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.idealFor.toLowerCase().includes(searchQuery.toLowerCase()) ||
      p.explore.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const whatsappBaseUrl = "https://wa.me/917339504861?text=";

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 sm:px-6 md:px-10 max-w-7xl mx-auto flex flex-col gap-10 text-white selection:bg-red-600 selection:text-white">
      {/* ── 1. MODERN EXPLORE HERO HEADER ── */}
      <motion.div
        className="relative bg-gradient-to-r from-[#181a24] via-[#10121a] to-[#1a1c26] p-6 sm:p-10 md:p-12 rounded-3xl border border-red-600/30 backdrop-blur-2xl shadow-2xl overflow-hidden flex flex-col items-center text-center gap-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="absolute -top-24 -left-24 w-72 h-72 bg-red-600/20 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-red-800/20 blur-[140px] rounded-full pointer-events-none" />
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-red-600 to-transparent" />

        <span className="inline-flex items-center gap-2 bg-red-600/15 border border-red-500/30 text-red-400 text-xs font-black uppercase tracking-[0.2em] px-4 py-1.5 rounded-full shadow-lg">
          <FaFire className="text-red-500" /> Individualized Training Library
        </span>

        <h1 className="text-3xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-none">
          WORKOUT <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 via-red-400 to-red-600">BLUEPRINTS</span>
        </h1>

        <p className="text-gray-300 text-xs sm:text-sm md:text-base max-w-2xl leading-relaxed">
          Explore our 9 specialized workout programs designed for strength, muscle building, fat loss, and athletic performance.
        </p>

        {/* Search & Filter Bar */}
        <div className="w-full max-w-xl flex flex-col sm:flex-row items-center gap-3 mt-2">
          <div className="w-full relative">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="text"
              placeholder="Search by goal or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#0c0d12]/90 border border-white/15 focus:border-red-500 text-white text-xs sm:text-sm rounded-2xl pl-11 pr-4 py-3 shadow-inner outline-none transition-all placeholder:text-gray-500"
            />
          </div>
        </div>

        {/* Category Pills Bar */}
        <div className="w-full overflow-x-auto py-2 no-scrollbar flex items-center gap-2 justify-start sm:justify-center">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-all whitespace-nowrap cursor-pointer border flex-shrink-0 ${
                activeCategory === cat
                  ? 'bg-red-600 text-white border-red-500 shadow-lg shadow-red-600/40 scale-105'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:bg-white/10 hover:text-white'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </motion.div>

      {/* ── 2. NEW DUAL INTERACTIVE VIEW SYSTEM (SPOTLIGHT + CARD GRID) ── */}
      <section className="flex flex-col gap-8">
        {/* Navigation Tabs Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-white/10 pb-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-black uppercase text-white flex items-center gap-2">
              <FaSlidersH className="text-red-500 text-lg" /> Program Spotlight & Blueprints
            </h2>
            <p className="text-xs text-gray-400 mt-1">Select a program to inspect its schedule, sample exercises, and progression methodology.</p>
          </div>

          <span className="text-xs font-bold text-red-400 bg-red-600/10 px-3 py-1.5 rounded-xl border border-red-500/20">
            Showing {filteredPrograms.length} of {programsData.length} Blueprints
          </span>
        </div>

        {/* Main Split Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Left Column: Interactive Program Pills List (4 Cols) */}
          <div className="lg:col-span-4 flex flex-col gap-3 max-h-[640px] overflow-y-auto pr-1 scrollbar-thin">
            {filteredPrograms.map((program, idx) => {
              const isActive = program.id === activeProgram.id;
              return (
                <button
                  key={program.id}
                  id={`card-${program.id}`}
                  onClick={() => setSelectedId(program.id)}
                  className={`text-left p-4 rounded-2xl border transition-all duration-300 flex items-center justify-between gap-3 group relative overflow-hidden cursor-pointer ${
                    isActive
                      ? 'bg-gradient-to-r from-red-600/25 via-[#1e202b] to-[#151722] border-red-500 shadow-xl shadow-red-600/20 ring-1 ring-red-500/40'
                      : 'bg-[#12141c]/90 border-white/10 hover:border-white/20 hover:bg-[#181a24]'
                  }`}
                >
                  {isActive && <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-red-500" />}

                  <div className="flex items-center gap-3.5">
                    <div
                      className={`w-11 h-11 rounded-xl flex items-center justify-center transition-all ${
                        isActive
                          ? 'bg-red-600 text-white shadow-md'
                          : 'bg-white/5 text-red-400 group-hover:bg-red-600/20'
                      }`}
                    >
                      {program.icon}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] font-black uppercase tracking-widest text-red-400">
                        0{idx + 1} • {program.category}
                      </span>
                      <span className="text-xs sm:text-sm font-black uppercase text-white group-hover:text-red-400 transition-colors">
                        {program.shortTitle}
                      </span>
                    </div>
                  </div>

                  <FaChevronRight
                    className={`text-xs transition-transform ${
                      isActive ? 'text-red-500 translate-x-1' : 'text-gray-600 group-hover:text-gray-300'
                    }`}
                  />
                </button>
              );
            })}
          </div>

          {/* Right Column: Detailed Active Spotlight View (8 Cols) */}
          <div className="lg:col-span-8">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeProgram.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.3 }}
                className="bg-gradient-to-b from-[#191b26] to-[#12141c] p-6 sm:p-8 rounded-3xl border border-red-500/50 shadow-2xl flex flex-col gap-6 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 w-64 h-64 bg-red-600/10 blur-[100px] rounded-full pointer-events-none" />
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-transparent via-red-500 to-transparent" />

                {/* Card Title & Header */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-white/10 pb-5">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-red-600 to-red-800 text-white flex items-center justify-center shadow-lg shadow-red-600/30 [&>svg]:w-7 [&>svg]:h-7 flex-shrink-0">
                      {activeProgram.icon}
                    </div>
                    <div>
                      <span className="text-[10px] font-black uppercase tracking-widest text-red-400 bg-red-600/15 px-3 py-1 rounded-full border border-red-500/30">
                        Category: {activeProgram.category}
                      </span>
                      <h2 className="text-2xl sm:text-3xl font-black uppercase text-white tracking-wide mt-1.5">
                        {activeProgram.heading}
                      </h2>
                    </div>
                  </div>

                  <a
                    href={`${whatsappBaseUrl}${encodeURIComponent(
                      `Hi IDOL Fitness Zone, I want to inquire about the ${activeProgram.heading} program!`
                    )}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-500 hover:to-red-600 text-white font-black text-xs uppercase tracking-wider px-5 py-3 rounded-xl shadow-lg shadow-red-600/30 transition-all hover:scale-105 self-start sm:self-auto whitespace-nowrap"
                  >
                    <FaWhatsapp className="text-base text-green-400" /> Start Blueprint
                  </a>
                </div>

                {/* Ideal For & Explore Breakdown */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-[#0e1017] border border-red-500/30 p-5 rounded-2xl flex flex-col gap-2 shadow-md relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
                    <span className="text-xs font-black uppercase tracking-widest text-red-400 flex items-center gap-1.5">
                      <FaCheckCircle className="text-red-500" /> Ideal For
                    </span>
                    <p className="text-xs sm:text-sm text-gray-200 leading-relaxed pt-1">
                      {activeProgram.idealFor}
                    </p>
                  </div>

                  <div className="bg-[#0e1017] border border-red-500/30 p-5 rounded-2xl flex flex-col gap-2 shadow-md relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
                    <span className="text-xs font-black uppercase tracking-widest text-red-400 flex items-center gap-1.5">
                      <FaFire className="text-red-500" /> Explore Methodology
                    </span>
                    <p className="text-xs sm:text-sm text-gray-200 leading-relaxed pt-1">
                      {activeProgram.explore}
                    </p>
                  </div>
                </div>

                {/* Weekly Schedule */}
                {activeProgram.weeklyPlan && (
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-extrabold uppercase tracking-widest text-gray-300 flex items-center gap-2">
                      <FaCalendarWeek className="text-red-500" /> Weekly Schedule Outline
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                      {activeProgram.weeklyPlan.map((plan, pIdx) => (
                        <div
                          key={pIdx}
                          className="bg-[#0b0c10] border border-white/10 p-3 rounded-xl flex items-center justify-between text-xs hover:border-red-500/30 transition-colors"
                        >
                          <span className="font-extrabold text-red-400 bg-red-600/10 px-2 py-0.5 rounded-lg border border-red-500/20 text-[10px]">
                            {plan.day}
                          </span>
                          <span className="text-gray-200 font-semibold text-right text-[11px]">
                            {plan.activity}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sample Workout */}
                {activeProgram.sampleWorkout && (
                  <div className="flex flex-col gap-3">
                    <span className="text-xs font-extrabold uppercase tracking-widest text-gray-300 flex items-center gap-2">
                      <FaDumbbell className="text-red-500" /> {activeProgram.sampleWorkoutTitle}
                    </span>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                      {activeProgram.sampleWorkout.map((ex, eIdx) => (
                        <div
                          key={eIdx}
                          className="bg-[#0b0c10] border border-white/10 p-3 rounded-xl flex flex-col justify-between gap-1 hover:border-red-500/30 transition-colors"
                        >
                          <span className="text-xs font-bold text-white">{ex.exercise}</span>
                          <span className="text-[10px] font-black uppercase text-red-400 tracking-wider">
                            {ex.prescribe}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Progression Rule */}
                <div className="bg-[#07080b] border border-white/10 p-4 rounded-2xl flex items-start gap-3">
                  <FaChartLine className="text-red-500 text-lg flex-shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-0.5">
                    <span className="text-[10px] font-extrabold uppercase tracking-wider text-red-400">
                      Progression Rule
                    </span>
                    <p className="text-xs text-gray-300 leading-relaxed">
                      {activeProgram.progression}
                    </p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ExplorePrograms;
